/// <amd-module name="Reporting/VelocityReport/VelocityReport" />
import { Backlog } from "Analytics/Boards/BoardsQueries/BacklogsQuery";
import * as React from "react";
import { BoardsReport, BoardsReportState } from "Reporting/BoardsCommon/BoardsReport";
import { getBoardsReportUrl } from "Reporting/BoardsCommon/BoardsRoutingHelper";
import {
    AggregationPicker,
    AggregationSettings,
    getAggregationOptionsForBacklogCategories,
    WorkItemAggregationOption
} from "Reporting/BoardsCommon/Pickers/AggregationPicker";
import { getInfoIcon, hasUserSeenBubble, ReportGuidedTourUtility } from "Reporting/BoardsCommon/ReportGuidedTourUtility";
import { resetQueryCache } from "Reporting/BoardsCommon/ReportQueryHelper";
import { publishEmptyReportTelemetry, publishReportTelemetry } from "Reporting/BoardsCommon/ReportTelemetry";
import { ResetElement } from "Reporting/BoardsCommon/ResetElement";
import * as CommonResources from "Reporting/BoardsCommon/Resources";
import { UnmaterializedBoardMessage } from "Reporting/BoardsCommon/UnmaterializedBoardMessage";
import { getBacklog } from "Reporting/VelocityReport/Pickers/BacklogsQuery";
import { customIterationsId, getIterationOptions } from "Reporting/VelocityReport/Pickers/IterationsPickerOptions";
import * as Resources from "Reporting/VelocityReport/Resources";
import { ObservableValue } from "VSS/Core/Observable";
import { ICancelablePromise } from "VSS/Core/Util/Promise";
import { format } from "VSS/Core/Util/String";
import { NoOpComponent } from "VSS/Platform/Components/Layout";
import { FastPageSwitch } from "VSS/Platform/FPS";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IButtonProps } from "VSSUI/Button";
import { FormItem } from "VSSUI/FormItem";
import { IListBoxItem } from "VSSUI/ListBox";
import { TextField, TextFieldWidth } from "VSSUI/TextField";
import { GenericSelectionDropdown } from "Widgets/Configs/Common/GenericSelectionDropdown";
import { VelocitySettings } from "Widgets/VelocityWidget/VelocityWidgetComponents/VelocitySettings";
import { packSettings } from "Widgets/VelocityWidget/VelocityWidgetComponents/VelocitySettingsManager";
import { getTeachingBubblesData } from "./VelocityReportTeachingBubbleHelper";
import { getVelocityReportSettingsStorageKey, VelocityReportUserSettings, VelocityReportUserSettingsData } from "./VelocityReportUserSettings";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { Checkbox } from "VSSUI/Checkbox";

export interface VelocityReportProps {
    projectId: string;
    teamId: string;
    teamName: string;

    /** The invariant category reference Name used to reliably identify a backlog */
    categoryReferenceName: string;

    /** The user facing name of backlog, used for routing  */
    categoryName: string;

    /** Requests a refresh of current state. */
    lastRefresh?: Date;

    /** Signal handler for load completion, used for TTI lifecycle of parent page. */
    onLoadCompleted?: () => void;
}

interface VelocityReportState extends BoardsReportState<VelocitySettings> {
    aggregationOptions: WorkItemAggregationOption[];
    iterationOptions: IListBoxItem<number>[];
    isCustomIteration?: boolean;
    customIterationValidationMessage: string | null;
    showTeachingBubble?: boolean;
    hasCustomUserSettings?: boolean;
}

export class VelocityReport extends BoardsReport<VelocityReportProps, VelocitySettings, VelocityReportState> {
    private textFieldValue: ObservableValue<string> = new ObservableValue<string>("");
    private reportGuidedTourUtility: ReportGuidedTourUtility;

    private backlogCanceleablePromise: ICancelablePromise<Backlog | undefined>;
    private aggregationCanceleablePromise: ICancelablePromise<WorkItemAggregationOption[]>;

    constructor(props: VelocityReportProps, context?: IVssComponentContext) {
        super(props, context);
        this.state = this.getDefaultState();
    }

    public getReportTitle(): string {
        return Resources.ReportTitle;
    }

    public getReportTelemetryName(): string {
        return "VelocityReport";
    }

    public getWidgetName(): string {
        const teamName = this.props.teamName;
        return format(Resources.VelocityWidgetTitleFormat, teamName);
    }

    public getWidgetContributionId(): string {
        return "ms.vss-dashboards-web.Microsoft.VisualStudioOnline.Dashboards.VelocityWidget";
    }

    public initializeOnRender(): void {
        this.reportGuidedTourUtility = new ReportGuidedTourUtility(this.context.pageContext, getTeachingBubblesData(), this.onBubbleDismiss);
    }

    public renderTeachingBubble(): JSX.Element {
        return <> {this.reportGuidedTourUtility.render(this.state.showTeachingBubble)} </>;
    }

    public getTitleBlock(): JSX.Element {
        const showIcon = this.isReady() && !this.isInAbnormalState() && this.isConfigurationValid();
        return (
            <>
                {super.getTitleBlock(
                    getInfoIcon(this.reportGuidedTourUtility.titleBubbleCallback, this.toggleTeachingBubble, showIcon, this.getReportTitle())
                )}
            </>
        );
    }
    public decorateWidgetContent(widgetContent: JSX.Element | null): JSX.Element {
        return <div ref={this.reportGuidedTourUtility.chartBubbleCallback}>{widgetContent}</div>;
    }
    public getFilterItems(): JSX.Element {
        return (
            <>
                <div ref={this.reportGuidedTourUtility.filterBubbleCallback}>
                    <FormItem label={Resources.AggregateByLabel}>
                        <AggregationPicker
                            value={this.state.widgetSettings!.aggregation}
                            items={this.state.aggregationOptions}
                            onSelect={this.onAggregationSelect}
                        />
                    </FormItem>
                </div>
                <FormItem label={Resources.NumberOfIterationsLabel}>
                    <GenericSelectionDropdown
                        selectedId={this.state.isCustomIteration ? customIterationsId : this.state.widgetSettings!.numberOfIterations.toString()}
                        items={this.state.iterationOptions}
                        onSelect={this.onIterationSelect}
                    />
                </FormItem>
                {this.state.isCustomIteration && (
                    <FormItem
                        className="custom-iterations-form-item"
                        label={Resources.CustomNumberOfIterationsLabel}
                        message={this.state.customIterationValidationMessage}
                        error={this.state.customIterationValidationMessage !== null}
                    >
                        <TextField
                            ariaLabel={Resources.CustomIterations}
                            value={this.textFieldValue}
                            onChange={this.onCustomIterationChanged}
                            width={TextFieldWidth.auto}
                            className={"custom-iterations-text-field"}
                            inputClassName={"custom-iterations-text-field-input"}
                        />
                    </FormItem>
                )}
                {
                    isFeatureFlagEnabled(this.context.pageContext, "WebAccess.Widgets.ShowResolvedItemsAsCompleted", false) &&
                    <FormItem label={Resources.AdvancedLabel}>
                        <Checkbox
                            label={this.renderCheckboxLabelWithInfoIcon(Resources.ShowResolvedItemsAsCompletedLabel)}
                            onChange={this.onToggleShowResolvedItemsAsCompleted}
                            checked={this.state.widgetSettings?.showResolvedItemsAsCompleted ?? false}
                            tooltipProps={{text: Resources.ShowResolvedItemsAsCompletedLabelToolTipText}}
                        />
                    </FormItem>
                } 
                <ResetElement onClick={this.resetSettings} isEnabled={this.state.hasCustomUserSettings} />
            </>
        );
    }

    public getKnownWarningComponent(): JSX.Element | null {
        if (this.state.knownWarning === Resources.MissingBacklogDetail) {
            return <UnmaterializedBoardMessage />;
        }
        return null;
    }

    public componentDidMount() {
        resetQueryCache(this.context.pageContext);
        this.initializeReportState();
    }

    public componentDidUpdate(prevProps: VelocityReportProps) {
        if (JSON.stringify(this.props) !== JSON.stringify(prevProps)) {
            if (this.props.lastRefresh !== prevProps.lastRefresh) {
                resetQueryCache(this.context.pageContext);
            }

            this.setState(this.getDefaultState());
            this.initializeReportState();
        }
    }

    public async initializeReportState() {
        this.cancelDataPromises();

        try {
            // TODO Read settings from URL parameters
            const userSettings: VelocityReportUserSettingsData = await VelocityReportUserSettings.load(
                this.context.pageContext,
                this.props.teamId,
                this.props.categoryReferenceName
            );

            const hasUserDismissedBubble = await hasUserSeenBubble(this.context.pageContext, getVelocityReportSettingsStorageKey());

            this.backlogCanceleablePromise = this.trackPromise(
                getBacklog(this.context.pageContext, this.props.projectId, this.props.teamId, this.props.categoryReferenceName)
            );
            const backlog = await this.backlogCanceleablePromise.promise;
            if (backlog == null) {
                const warningMessage = Resources.MissingBacklogDetail;
                this.setState({
                    knownWarning: warningMessage
                });
                publishEmptyReportTelemetry(this.context.pageContext, this.getReportTelemetryName(), warningMessage);
                return;
            }

            const iterationOptions = getIterationOptions();

            this.aggregationCanceleablePromise = this.trackPromise(
                getAggregationOptionsForBacklogCategories(this.context.pageContext, this.props.projectId, this.props.teamId, [
                    this.props.categoryReferenceName
                ])
            );
            const aggregationOptions = await this.aggregationCanceleablePromise.promise;

            const numberOfIterations = userSettings.numberOfIterations || 6;
            const widgetSettings = packSettings(this.props, backlog.BoardCategoryReferenceName, userSettings.aggregationSettings, numberOfIterations, userSettings.showResolvedItemsAsCompleted ?? false);

            const isCustomIteration = this.isCustomIteration(iterationOptions, numberOfIterations);

            this.setState({
                aggregationOptions: aggregationOptions,
                iterationOptions: iterationOptions,
                widgetSettings: widgetSettings,
                isCustomIteration: isCustomIteration,
                showTeachingBubble: !hasUserDismissedBubble,
                hasCustomUserSettings: JSON.stringify(userSettings) !== "{}"
            });

            // Set user's custom iteration value to text field here
            if (isCustomIteration) {
                this.textFieldValue.value = this.state.widgetSettings!.numberOfIterations.toString();
            }

            publishReportTelemetry(this.context.pageContext, this.getReportTelemetryName(), {
                aggregationMode: widgetSettings.aggregation.identifier,
                // TODO: Anonymized Aggregation field
                numberOfIterations: widgetSettings.numberOfIterations,
                showResolvedItemsAsCompleted: widgetSettings.showResolvedItemsAsCompleted
            });
        } catch (e) {
            if (e.isCanceled) {
                return; // Do nothing if the promises were cancelled
            }
            this.handleCommonInitError(e);
        } finally {
            if (this.props.onLoadCompleted) {
                this.props.onLoadCompleted();
            }
        }
    }

    public getDefaultState(): VelocityReportState {
        return {
            aggregationOptions: [],
            iterationOptions: [],
            customIterationValidationMessage: null,
            ...super.getDefaultState()
        };
    }

    public isConfigurationValid(): boolean {
        return this.state.customIterationValidationMessage === null;
    }

    protected getBackButtonProps(): IButtonProps | undefined {
        return {
            onClick: () => {
                const destination = getBoardsReportUrl(this.context.pageContext, this.props.categoryName);
                const recordHistoryEntry = true;
                FastPageSwitch(this.context.pageContext, destination, recordHistoryEntry);
            }
        };
    }
    private cancelDataPromises(): void {
        if (this.backlogCanceleablePromise) {
            this.backlogCanceleablePromise.cancel();
        }

        if (this.aggregationCanceleablePromise) {
            this.aggregationCanceleablePromise.cancel();
        }
    }

    private resetSettings = async (): Promise<void> => {
        // wait to clear out previously saved settings, and re-initialize the report.
        await VelocityReportUserSettings.reset(this.context.pageContext, this.props.teamId, this.props.categoryReferenceName);
        this.initializeReportState();
    };

    private onAggregationSelect = (newAggregationField: AggregationSettings): void => {
        const newSettings = { ...this.state.widgetSettings! };
        newSettings.aggregation = newAggregationField;
        this.setUserSelection(newSettings);
    };

    private onIterationSelect = (newIterationPeriod: IListBoxItem<number>): void => {
        // If user selects the custom iteration option in drop down , set flag to show textfield  else unset the flag
        const customPeriodSelected = newIterationPeriod.id === customIterationsId;

        if (customPeriodSelected) {
            this.textFieldValue.value = this.state.widgetSettings!.numberOfIterations.toString();
            // If you had previously left the custom field with an error,
            // going back into it with a new valid value should not keep the error.
            this.setState({ customIterationValidationMessage: null });
        } else {
            const newSettings = { ...this.state.widgetSettings! };
            newSettings.numberOfIterations = newIterationPeriod.data!;
            this.setUserSelection(newSettings);
        }

        this.setState({ isCustomIteration: customPeriodSelected });
    };

    private onCustomIterationChanged = (_event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, value: string): void => {
        // Ensure value entered by user is on display in every case
        const newValue = value.replace(/\D/g, "");
        this.textFieldValue.value = newValue;
        this.validateAndSaveCustomIterationsInput(newValue);
    };

    // Handle custom iterations input in 4 cases : empty textfield , valid number , 0 and not a number
    private validateAndSaveCustomIterationsInput(value: string) {
        const customNumberOfIterations = Number(value);
        const minimumIterations = 1;
        const maximumIterations = 15;

        if (value !== "" && customNumberOfIterations >= minimumIterations && customNumberOfIterations <= maximumIterations) {
            this.setState({ customIterationValidationMessage: null });
            const newSettings = { ...this.state.widgetSettings! };
            newSettings.numberOfIterations = customNumberOfIterations;
            this.setUserSelection(newSettings);
        } else {
            this.setState({ customIterationValidationMessage: format(CommonResources.ValueMustBeWithinRange, minimumIterations, maximumIterations) });
        }
    }

    // Is any value in the default iteration options mapping to the iterations in the state obtained from saved user settings
    private isCustomIteration(iterationOptions: IListBoxItem<number>[], numberOfIterations: number): boolean {
        return !iterationOptions.some(item => {
            return item.data != null && item.data === numberOfIterations;
        });
    }

    private toggleTeachingBubble = (): void => {
        this.setState({
            showTeachingBubble: !this.state.showTeachingBubble
        });
    };

    private onBubbleDismiss = (): void => {
        this.setState({
            showTeachingBubble: false
        });
    };

    private setUserSelection(newSettings: VelocitySettings) {
        VelocityReportUserSettings.save(this.context.pageContext, newSettings, this.props.teamId, this.props.categoryReferenceName);
        this.setState({ widgetSettings: newSettings, hasCustomUserSettings: true });
    }

    /* render ReactNode with labelText and an info icon at end of the text */
    private renderCheckboxLabelWithInfoIcon = (labelText: string) : React.ReactNode => {
        return (
            <div>
                <span>{labelText}</span> &#9432;
            </div>
        )
    }

    private onToggleShowResolvedItemsAsCompleted = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, showResolvedItemsAsCompleted: boolean): void => {
        const newSettings = { ...this.state.widgetSettings! };
        newSettings.showResolvedItemsAsCompleted = showResolvedItemsAsCompleted;
        this.setUserSelection(newSettings);
    };
}

VssComponent.register("VelocityReport", VelocityReport);
VssComponent.register("VelocityReportPreloader", NoOpComponent);
